import React from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { UsersRound, Shield, UserCheck, Mail, X } from "lucide-react";

/**
 * CreateUserModal — Diálogo de alta de usuario (proveedor de auth, datos, roles, responsable).
 *
 * Extraído 1:1 desde `pages/UsersPage.js`. Permite elegir proveedor de autenticación
 * (Microsoft SSO recomendado o email/contraseña local), datos básicos, roles y responsable
 * (para COMERCIAL). La lógica de creación permanece en la página (`handleCreateUser`).
 *
 * @param {boolean}  props.createModalOpen         Apertura del diálogo.
 * @param {Function} props.setCreateModalOpen       Setter del flag de apertura.
 * @param {Object}   props.newUser                  Estado del formulario del nuevo usuario.
 * @param {Function} props.setNewUser               Setter del formulario.
 * @param {string[]} props.availableRoles           Roles disponibles.
 * @param {Function} props.handleNewUserRoleToggle  (role) => void — alterna un rol.
 * @param {Function} props.getRoleColor             (role) => string — clases de color del badge.
 * @param {Function} props.getRoleLabel             (role) => string — etiqueta del rol.
 * @param {Array}    props.managers                 Usuarios que pueden ser responsables.
 * @param {Function} props.handleCreateUser         Crea el usuario.
 * @param {boolean}  props.creating                 Estado de creación en curso (deshabilita el botón).
 */
export const CreateUserModal = ({
  createModalOpen,
  setCreateModalOpen,
  newUser,
  setNewUser,
  availableRoles,
  handleNewUserRoleToggle,
  getRoleColor,
  getRoleLabel,
  managers,
  handleCreateUser,
  creating,
}) => {
  return (
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="bg-[#0A0A35] border-[#1E1E5F] text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <UsersRound className="h-5 w-5 text-[#42E695]" />
              Nuevo Usuario
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            <div className="space-y-3">
              <Label className="text-slate-300">Tipo de Autenticación</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setNewUser({ ...newUser, auth_provider: "microsoft" })}
                  className={`p-4 rounded-lg border transition-all ${
                    newUser.auth_provider === "microsoft"
                      ? "bg-[#00a4ef]/20 border-[#00a4ef] text-white"
                      : "bg-[#1B1B4B]/50 border-[#1E1E5F] text-slate-400 hover:border-[#00a4ef]/50"
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 21 21">
                      <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                      <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                      <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                      <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                    </svg>
                    <span className="text-sm font-medium">Microsoft</span>
                    <span className="text-xs text-slate-500">Recomendado</span>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setNewUser({ ...newUser, auth_provider: "local" })}
                  className={`p-4 rounded-lg border transition-all ${
                    newUser.auth_provider === "local"
                      ? "bg-[#42E695]/20 border-[#42E695] text-white"
                      : "bg-[#1B1B4B]/50 border-[#1E1E5F] text-slate-400 hover:border-[#42E695]/50"
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <Mail className="h-6 w-6" />
                    <span className="text-sm font-medium">Email/Contraseña</span>
                    <span className="text-xs text-slate-500">Login local</span>
                  </div>
                </button>
              </div>
              {newUser.auth_provider === "microsoft" && (
                <p className="text-xs text-slate-400 mt-2">
                  El usuario iniciará sesión con su cuenta de Microsoft (@wilan.es)
                </p>
              )}
            </div>

            <div className="space-y-4 pt-4 border-t border-[#1E1E5F]">
              <h3 className="text-[#03BEE4] font-medium flex items-center gap-2">
                <UsersRound className="h-4 w-4" />
                Información Básica
              </h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label className="text-slate-300">Nombre Completo *</Label>
                  <Input
                    value={newUser.name}
                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    placeholder="Ej: María García"
                    className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                    data-testid="new-user-name"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Email *</Label>
                  <Input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    placeholder={newUser.auth_provider === "microsoft" ? "usuario@wilan.es" : "usuario@email.com"}
                    className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                    data-testid="new-user-email"
                  />
                </div>
                {newUser.auth_provider === "local" && (
                  <div>
                    <Label className="text-slate-300">Contraseña *</Label>
                    <Input
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      placeholder="Mínimo 6 caracteres"
                      className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                      data-testid="new-user-password"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t border-[#1E1E5F]">
              <h3 className="text-[#03BEE4] font-medium flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Roles del Usuario
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {availableRoles.map(role => (
                  <div
                    key={role}
                    onClick={() => handleNewUserRoleToggle(role)}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      newUser.roles.includes(role)
                        ? "bg-[#1B1B4B] border-[#42E695]/50"
                        : "bg-[#1B1B4B]/50 border-[#1E1E5F] hover:border-[#42E695]/30"
                    }`}
                  >
                    <Checkbox 
                      checked={newUser.roles.includes(role)}
                      className="border-[#1E1E5F] data-[state=checked]:bg-[#42E695] data-[state=checked]:border-[#42E695]"
                    />
                    <Badge className={`${getRoleColor(role)} text-white text-xs`}>
                      {getRoleLabel(role)}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>

            {newUser.roles.includes("COMERCIAL") && (
              <div className="space-y-4 pt-4 border-t border-[#1E1E5F]">
                <h3 className="text-[#03BEE4] font-medium flex items-center gap-2">
                  <UserCheck className="h-4 w-4" />
                  Responsable
                </h3>
                <p className="text-xs text-slate-400">El responsable podrá ver y gestionar las oportunidades y clientes de este comercial.</p>
                <Select
                  value={newUser.responsable_id || "__none__"}
                  onValueChange={(v) => {
                    if (v === "__none__") {
                      setNewUser({ ...newUser, responsable_id: null, responsable_nombre: null });
                    } else {
                      const mgr = managers.find(m => m.id === v);
                      setNewUser({ ...newUser, responsable_id: v, responsable_nombre: mgr?.name || "" });
                    }
                  }}
                >
                  <SelectTrigger className="bg-[#0A0A35] border-[#1E1E5F] text-white" data-testid="create-responsable-select">
                    <SelectValue placeholder="Sin responsable" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                    <SelectItem value="__none__" className="text-slate-400">Sin responsable</SelectItem>
                    {managers.map(m => (
                      <SelectItem key={m.id} value={m.id} className="text-white">{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t border-[#1E1E5F]">
              <Button
                variant="outline"
                onClick={() => {
                  setCreateModalOpen(false);
                  setNewUser({
                    name: "",
                    email: "",
                    password: "",
                    roles: ["COMERCIAL"],
                    notification_email: true,
                    notification_inapp: true,
                    notify_on_ticket_close: true,
                    notify_on_new_assignment: true,
                    is_active: true,
                    auth_provider: "microsoft",
                    responsable_id: null,
                    responsable_nombre: null,
                  });
                }}
                className="border-[#1E1E5F] text-slate-300"
              >
                <X className="h-4 w-4 mr-2" />
                Cancelar
              </Button>
              <Button 
                onClick={handleCreateUser} 
                disabled={creating}
                className="bg-[#42E695] text-[#04042C]" 
                data-testid="create-user-submit-btn"
              >
                {creating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#04042C] border-t-transparent mr-2" />
                    Creando...
                  </>
                ) : (
                  <>
                    <UsersRound className="h-4 w-4 mr-2" />
                    Crear Usuario
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
  );
};
